import { cn } from "@/lib/utils";
import { ManualAssistantMessage } from "@/store/zustand/aiContextStore";
import { Loader2, ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";

export interface CommitProps {
  progressItems?: ManualAssistantMessage["progressItems"];
  groupTitle?: string; // If provided, items will be grouped under this collapsible title
}

export const ProgressBlock = React.memo(
  ({ progressItems, groupTitle }: CommitProps) => {
    const [isExpanded, setIsExpanded] = useState(true);

    // Early return if no items to show
    if (!progressItems || progressItems.length === 0) {
      return null;
    }

    const showGrouped = !!groupTitle;
    const shouldShowItems = !showGrouped || isExpanded;

    return (
      <div
        className={cn(
          "flex flex-col grow bg-secondary-200 px-1 rounded-md border border-[#2F3035] mt-2 mb-2",
        )}
      >
        {showGrouped && (
          <div
            className="flex items-center gap-1 p-2 cursor-pointer"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            <div className="text-xs font-medium">{groupTitle}</div>
          </div>
        )}

        {shouldShowItems && (
          <div
            className={cn(
              "flex flex-col",
              showGrouped && "border-t border-[#2F3035] -mx-1 px-1",
            )}
          >
            {progressItems.map((item, index) => {
              const showFailed = item.failed;
              const showSucceeded = item.succeeded;
              const showLoading =
                item.loading && !item.failed && !item.succeeded;

              return (
                <div
                  key={index}
                  className="flex flex-row gap-1 items-center justify-start p-2"
                >
                  {(showFailed || showSucceeded || showLoading) && (
                    <div className={cn(showLoading && "animate-spin")}>
                      {showFailed ? (
                        <img
                          src="/icons/ai-chat-status/failed.svg"
                          className="size-4 shrink-0"
                        />
                      ) : showSucceeded ? (
                        <img
                          src="/icons/ai-chat-status/success.svg"
                          className="size-4 shrink-0"
                        />
                      ) : (
                        <Loader2 className="size-4" />
                      )}
                    </div>
                  )}

                  <div className="rounded-md text-xs bg-secondary-n-950 text-secondary-n-50 break-all">
                    {item.title}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  },
);
